/**
 * EDIÇÃO DE APONTAMENTO DE RDO.
 *
 * Editar é UPDATE no registro existente — o apontamento mantém o seu id, a
 * sua data de criação e quem o criou. Não há DELETE + CREATE em lugar nenhum
 * deste fluxo.
 *
 * Um apontamento deixa de ser editável quando já entrou num RDO Consolidado
 * aprovado/assinado, ou quando o próprio apontamento foi emitido: a partir
 * daí a correção passa a exigir revisão documental.
 */

export type ConsolidatedStatus = string | null | undefined;

/** Estados em que o consolidado congela os apontamentos que o compõem. */
export const FROZEN_CONSOLIDATED_STATUSES = ["APPROVED", "SIGNED", "ISSUED", "FROZEN"] as const;

export const ENTRY_FROZEN_MESSAGE =
  "Este apontamento já está vinculado a um RDO consolidado aprovado/assinado. Para alterar, gere uma revisão conforme o fluxo documental.";

export const ENTRY_ISSUED_MESSAGE =
  "Este apontamento já foi emitido e não pode ser alterado. Gere uma revisão conforme o fluxo documental.";

export const ENTRY_DELETED_MESSAGE =
  "Este apontamento foi excluído e não pode ser editado.";

export function isConsolidatedFrozen(status: ConsolidatedStatus): boolean {
  const value = String(status ?? "").toUpperCase();
  return (FROZEN_CONSOLIDATED_STATUSES as readonly string[]).includes(value);
}

export interface EntryEditability {
  editable: boolean;
  /** Mensagem exata a exibir quando a edição está bloqueada. */
  reason: string | null;
  /** Consolidado que congelou o apontamento, quando for esse o motivo. */
  frozenByConsolidatedId: string | null;
}

/**
 * Consolidado ainda em rascunho/elaboração não congela nada: a edição segue
 * normal e o documento passa a refletir a versão atualizada.
 */
export function resolveEntryEditability(input: {
  entry: { status?: string | null; deletedAt?: string | null } | null;
  consolidations: readonly { id: string; status?: string | null }[];
}): EntryEditability {
  const { entry, consolidations } = input;

  if (!entry) {
    return { editable: false, reason: ENTRY_DELETED_MESSAGE, frozenByConsolidatedId: null };
  }
  if (entry.deletedAt) {
    return { editable: false, reason: ENTRY_DELETED_MESSAGE, frozenByConsolidatedId: null };
  }
  if (String(entry.status ?? "").toUpperCase() === "ISSUED") {
    return { editable: false, reason: ENTRY_ISSUED_MESSAGE, frozenByConsolidatedId: null };
  }

  const frozen = consolidations.find(consolidated => isConsolidatedFrozen(consolidated.status));
  if (frozen) {
    return { editable: false, reason: ENTRY_FROZEN_MESSAGE, frozenByConsolidatedId: frozen.id };
  }

  return { editable: true, reason: null, frozenByConsolidatedId: null };
}

/** Campos do apontamento que a auditoria acompanha. */
export const AUDITED_ENTRY_FIELDS = [
  "professionalId",
  "projectId",
  "serviceFrontId",
  "workOrderId",
  "unitAreaId",
  "subWorkOrderId",
  "date",
  "shift",
  "startTime",
  "endTime",
  "normalHours",
  "overtimeHours",
  "om",
  "functionName",
  "description",
  "inspectorComment",
  "status"
] as const;

/**
 * Campos cuja alteração muda a que documento o apontamento pertence ou de
 * quem ele é — a tela confirma antes de salvar.
 */
export const CONFIRMATION_FIELDS = [
  "professionalId",
  "date",
  "projectId",
  "serviceFrontId",
  "workOrderId",
  "subWorkOrderId"
] as const;

export interface FieldChange {
  field: string;
  previousValue: unknown;
  newValue: unknown;
}

/**
 * Uma edição registrada no próprio apontamento.
 *
 * O histórico vive no registro, e não no AuditRecorder, porque o app real
 * injeta um recorder que descarta os eventos — um histórico lido de lá
 * apareceria sempre vazio. Os eventos de auditoria continuam sendo emitidos
 * em paralelo, para quando houver um armazenamento de auditoria de verdade.
 */
export interface RdoEntryChangeRecord {
  changedAt: string;
  changedByUserId: string | null;
  changedByProfessionalId: string | null;
  version: number;
  changes: FieldChange[];
}

/** Rótulos dos campos, para o histórico ser legível por quem opera. */
export const ENTRY_FIELD_LABELS: Record<string, string> = {
  professionalId: "Colaborador",
  projectId: "Projeto",
  serviceFrontId: "Frente de Serviço",
  workOrderId: "Ordem de Serviço",
  subWorkOrderId: "Sub-OS",
  unitAreaId: "Unidade / Área",
  date: "Data do Apontamento",
  shift: "Turno",
  startTime: "Hora Inicial",
  endTime: "Hora Final",
  normalHours: "Horas Normais",
  overtimeHours: "Horas Extras",
  om: "OM",
  functionName: "Função / Cargo",
  description: "Descrição do Serviço",
  inspectorComment: "Comentário do Fiscal",
  status: "Status"
};

export function formatEntryFieldLabel(field: string): string {
  return ENTRY_FIELD_LABELS[field] ?? field;
}

export function appendEntryChangeHistory(
  history: readonly RdoEntryChangeRecord[] | undefined,
  record: RdoEntryChangeRecord
): RdoEntryChangeRecord[] {
  return [...(history ?? []), record];
}

const normalize = (value: unknown): unknown => {
  if (value === undefined || value === null || value === "") return null;
  return value;
};

/** Diferença entre o apontamento salvo e o que está sendo gravado. */
export function diffRdoEntry(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  fields: readonly string[] = AUDITED_ENTRY_FIELDS
): FieldChange[] {
  const changes: FieldChange[] = [];

  for (const field of fields) {
    const previousValue = normalize(before[field]);
    const newValue = normalize(after[field]);
    if (previousValue !== newValue) {
      changes.push({ field, previousValue, newValue });
    }
  }

  return changes;
}

/** true quando a alteração toca um campo que exige confirmação do usuário. */
export function requiresConfirmation(changes: readonly FieldChange[]): boolean {
  return changes.some(change => (CONFIRMATION_FIELDS as readonly string[]).includes(change.field));
}
